import React, { useEffect } from "react";
import { Stack, Text } from "sprint-common-web";

import {
    getOnPremAncillaryDetailsHref,
    getOnPremMovieDetailsHref,
    getOnPremTvSeriesDetailsHref
} from "../../../assets/AssetsRoutes";
import { AssetOperationType } from "../../../assets/common/types/AssetOperationType";
import { OnPremAssetStatus } from "../../../assets/details/on-prem-asset/OnPremAssetStatus";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { AssetOperationList, AssetOperationListItemWithHref } from "./AssetOperationList";
import { AssetOperationListItem, ContentDetails } from "./AssetOperationListItem";
import { useOnPremAssetOperations } from "./hooks/useOnPremAssetOperations";

interface OnPremAssetOperationListProps {
    fileRecordId?: string;
    setAssetContent?: (content: ContentDetails) => void;
}

export const OnPremAssetOperationList = ({
    fileRecordId,
    setAssetContent
}: OnPremAssetOperationListProps): JSX.Element | null => {
    const { data: onPremAssetOperations, isLoading } = useOnPremAssetOperations(fileRecordId);

    useEffect(() => {
        if (setAssetContent && onPremAssetOperations && onPremAssetOperations.length > 0) {
            setAssetContent(onPremAssetOperations[0].content);
        }
    }, [onPremAssetOperations, setAssetContent]);

    if (!fileRecordId) {
        return null;
    }

    const operationsWithHref: AssetOperationListItemWithHref[] = (onPremAssetOperations ?? []).map((item) => ({
        ...item,
        resultStatus: mapResultStatus(item.operationType),
        href: getOnPremAssetDetailsHref(item)
    }));

    if (!isLoading && !operationsWithHref.length) {
        return null;
    }

    return (
        <Stack>
            <Text variant="medium">
                <b>OnPrem Assets</b>
            </Text>
            <AssetOperationList operations={operationsWithHref} isLoading={isLoading} />
        </Stack>
    );
};

const mapResultStatus = (operationType: number) => {
    switch (operationType) {
        case AssetOperationType.New:
        case AssetOperationType.Retained:
            return OnPremAssetStatus[OnPremAssetStatus.Active];

        case AssetOperationType.Ignored:
        case AssetOperationType.Overridden:
            return OnPremAssetStatus[OnPremAssetStatus.Deprecated];

        default:
            throw Error(`Unsupported resulting operation type: ${operationType}`);
    }
};

const getOnPremAssetDetailsHref = (item: AssetOperationListItem): string | undefined => {
    if (item.content.contentType === ContentReferenceType.Movie) {
        return getOnPremMovieDetailsHref(item.id, item.content.subtype);
    }
    if (item.content.contentType === ContentReferenceType.TvSeries) {
        return getOnPremTvSeriesDetailsHref(item.id);
    }
    if (item.content.contentType === ContentReferenceType.Ancillary) {
        return getOnPremAncillaryDetailsHref(item.id, item.content.subtype);
    }

    return undefined;
};
